<script setup lang="ts">
import type { CardProps } from '~/utils/card'
import { faDiploma } from '@fortawesome/pro-light-svg-icons'
import { removeTrailingPeriod } from '~/utils/string'

const props = withDefaults(defineProps<Omit<CardProps, 'type'> & {
  image: string
  name: string
  bio?: string
  education?: string
}>(), {
  size: 'medium',
  orientation: 'vertical',
  presentation: false,
})

const cleanBio = computed(() => props.bio ? removeTrailingPeriod(props.bio) : '')
const cleanEducation = computed(() => props.education ? removeTrailingPeriod(props.education) : '')
</script>

<template>
  <Card type="author" v-bind="props">
    <template v-if="!presentation">
      <template v-if="orientation === 'vertical'">
        <div class="flex flex-col gap-s items-center">
          <img :src="image" :srcset="image" alt="" :class="cardVariants.image({ type: 'author', size, presentation })">
          <h5 :class="cardVariants.title({ type: 'author' })" v-text="name" />
        </div>
        <p class="text-caption text-center text-primary" v-text="cleanBio" />
      </template>

      <template v-if="orientation === 'horizontal'">
        <img :src="image" :srcset="image" alt="" :class="cardVariants.image({ type: 'author', size, presentation })">
        <div class="flex flex-col gap-xl">
          <h4 :class="cardVariants.title({ type: 'author' })" v-text="name" />
          <div class="flex flex-col gap-xs">
            <span class="text-caption" v-text="cleanEducation" />
            <p class="text-small text-primary" v-text="cleanBio" />
          </div>
        </div>
      </template>
    </template>

    <template v-else>
      <div :class="cardVariants.framed()">
        <ImageFramed
          :src="image"
          shape="hexagon"
          alt-text=""
          filled-background="white"
          class="stroke-moss-200"
        />
      </div>
      <div class="flex flex-col items-center gap-m">
        <div class="flex flex-col items-center gap-3xs">
          <h3 class="title-l" :class="cardVariants.title({ type: 'author', presentation })" v-text="name" />
          <div v-if="!!education" class="flex gap-2xs justify-center items-center text-moss-200">
            <Icon :icon="faDiploma" />
            <span class="text-small" v-text="cleanEducation" />
          </div>
        </div>
        <p class="text-small text-center text-moss-200" v-text="cleanBio" />
      </div>
    </template>
  </Card>
</template>
